import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { placeholderLogo } from 'images'
import labelPlaceholderLogo from 'images/icons/labelPlaceholder.png'
import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import PlayQueueActions, { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'

import {
  SearchCategory,
  SongTableColumns,
  NotificationType
} from 'constants/enums'

import {
  ReleasesMenu,
  ArtistsTable,
  LabelsTable,
  ReleasesTable,
  SongsTable
} from 'components'
import RecentlySearched from './RecentlySearched'
import ReleaseOptions from 'components/ReleaseOptions'

import { ResponsiveLayout } from 'containers'
import { browserHelpers } from 'utils'
import {
  ContentWrapper,
  ScrollContainer,
  Overlay,
  MobileTabsContainer,
  HalfCell,
  GridColumn,
  SeeAllContainer,
  MobileTopList
} from './Search.styles'
import {
  ReleaseSearchCard,
  TrackSearchCard,
  ArtistSearchCard,
  LabelSearchCard
} from 'components/SearchCards'
import ItemPreview from './ItemPreview'
import { Tonality } from '@dsny/dsny-component-library'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import useSearchParams from './hooks/useSearchParams'
import useSearchResults from './hooks/useSearchResults'
import getTopResultsUtil from './utils/topResults'
import styled, { useTheme } from 'styled-components'
import NoResults from './components/NoResults'
import CategorySection from './components/CategorySection'
import TopResultsMobile from './components/TopResultsMobile'
import TopResultsDesktop from './components/TopResultsDesktop'
import ResultRowMobile from './components/ResultRowMobile'
import { useSearchTracking } from './hooks/useSearchTracking'
import {
  SEARCH_TRACKING_EVENTS,
  SEARCH_ACTIONS,
  SEARCH_PLATFORMS
} from '../../constants/searchTracking'
import { checkFlag } from '@dsny/dsny-feature-flag-library'
import { useFeatureFlags } from 'contexts/FeatureFlagsContext'
import InfiniteScroll from 'external/infiniteScroll/app'
import ResponsiveBreakpointsRolloutBoundary from 'components/ResponsiveBreakpointsRolloutBoundary'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  ResponsiveBreakpointsProvider,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'

const CompactTabList = styled(Tonality.TabList)`
  & > div {
    display: flex;
    gap: 0;
  }
`

export const SearchContent = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const location = useLocation()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const { category, routeProfile, queryQ } = useSearchParams()
  const isMobile = useMobileMediaQuery()
  const { ready: featureFlagsReady } = useFeatureFlags()
  const showAIFilter = featureFlagsReady ? checkFlag('showAIFilter') : false
  const useAISearch = showAIFilter
  const playlists = useSelector((state) => state.playList.playlistAbstracts)
  const selectedGenres = useSelector((state) => state.genre.selectedGenres)
  const selectedMarkets = useSelector((state) => state.market.selectedMarkets)
  const isListView = useSelector((state) => state.setting.listView)
  const format = useSelector((state) => state.profile.profile.format)

  const [profile, setProfile] = useState(routeProfile)
  const [selectedCategory, setSelectedCategory] = useState(category)
  const tabIndexByCategory = useMemo(
    () => ({
      [SearchCategory.tracks]: 1,
      [SearchCategory.artists]: 2,
      [SearchCategory.releases]: 3,
      [SearchCategory.labels]: 4
    }),
    []
  )
  const categoryByTabIndex = useMemo(
    () => ({
      1: SearchCategory.tracks,
      2: SearchCategory.artists,
      3: SearchCategory.releases,
      4: SearchCategory.labels
    }),
    []
  )
  const { trackSearchEvent } = useSearchTracking()
  const platform = isMobile ? SEARCH_PLATFORMS.MOBILE : SEARCH_PLATFORMS.DESKTOP
  const [mobileTabIndex, setMobileTabIndex] = useState(0)
  const [mobileCategory, setMobileCategory] = useState(SearchCategory.top)
  const [isSearching, setIsSearching] = useState(false)
  const [searched, setSearched] = useState(queryQ)

  const handleSearchFocus = useCallback(() => {
    setIsSearching(true)
    trackSearchEvent?.({
      event: SEARCH_TRACKING_EVENTS.SEARCH_FOCUS,
      action: SEARCH_ACTIONS.FOCUS,
      label: searched || '',
      platform,
      source: 'search_page',
      type: 'search',
      active_category: selectedCategory || SearchCategory.top,
      search_profile: profile || '',
      search_query_length: (searched || '').length
    })
  }, [platform, profile, searched, selectedCategory, trackSearchEvent])

  const handleSearchUnfocus = useCallback(() => {
    setIsSearching(false)
    trackSearchEvent?.({
      event: SEARCH_TRACKING_EVENTS.SEARCH_UNFOCUS,
      action: SEARCH_ACTIONS.UNFOCUS,
      label: searched || '',
      platform,
      source: 'search_page',
      type: 'search',
      active_category: selectedCategory || SearchCategory.top,
      search_profile: profile || '',
      search_query_length: (searched || '').length
    })
  }, [platform, profile, searched, selectedCategory, trackSearchEvent])
  const {
    loading,
    response,
    update,
    hasMoreProfileResults,
    loadMoreProfileResults
  } = useSearchResults(useAISearch)
  const [showModal, setShowModal] = useState(false)
  const [modalTrack, setModalTrack] = useState(null)
  const searchBackTo = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.hash, location.pathname, location.search]
  )
  // response managed by hook

  const handleUpdateSearched = useCallback(
    (query, cat, prof) => {
      setIsSearching(false)
      setSearched(query)
      setSelectedCategory(cat)
      setProfile(prof)

      update(query, cat, prof, () => alert(t('alert.search-error')))
    },
    [update, t]
  )

  useEffect(() => {
    if (!featureFlagsReady) return
    handleUpdateSearched(queryQ, category, routeProfile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleUpdateSearched,
    queryQ,
    category,
    routeProfile,
    selectedGenres,
    selectedMarkets,
    featureFlagsReady
  ])

  const handlePlayRelease = useCallback(
    (release) =>
      dispatch(
        PlayQueueThunks.playRelease(release.releaseid, {
          source: PLAY_SOURCES.SEARCH,
          trigger: PLAY_TRIGGERS.RESULT
        })
      ),
    [dispatch]
  )

  useEffect(() => {
    const nextIndex =
      tabIndexByCategory[selectedCategory || SearchCategory.top] ?? 0
    setMobileTabIndex(nextIndex)
    setMobileCategory(categoryByTabIndex[nextIndex])
  }, [selectedCategory, tabIndexByCategory, categoryByTabIndex])

  const handleMobileTabChange = useCallback(
    (id) => {
      const idx = Number(id)
      setMobileTabIndex(idx)
      const cat = categoryByTabIndex[idx]
      if (!cat) return
      setMobileCategory(cat)
      trackSearchEvent?.({
        event: SEARCH_TRACKING_EVENTS.TAB_CHANGE,
        action: SEARCH_ACTIONS.TAB_CHANGE,
        label: cat,
        platform,
        source: 'search_page',
        type: 'tab',
        previous_tab: mobileCategory || SearchCategory.top,
        target_tab: cat,
        search_profile: profile || '',
        search_query: searched || '',
        search_query_length: (searched || '').length
      })
    },
    [
      categoryByTabIndex,
      mobileCategory,
      platform,
      profile,
      searched,
      trackSearchEvent
    ]
  )

  const handlePlaySong = useCallback(
    (song) =>
      dispatch(
        PlayQueueThunks.playSong(song, {
          source: PLAY_SOURCES.SEARCH,
          trigger: PLAY_TRIGGERS.RESULT
        })
      ),
    [dispatch]
  )

  const handleDownloadRelease = useCallback(
    (release) =>
      dispatch(
        DownloadsThunks.addReleaseToDownloadQueue(
          release,
          format,
          'search_results_release_card'
        )
      ),
    [dispatch, format]
  )

  const handleDownloadSong = useCallback(
    (song) =>
      dispatch(
        DownloadsThunks.addSongToDownloadQueue(song, {
          format,
          source: 'search_results_track_card'
        })
      ),
    [dispatch, format]
  )

  const handlePlayNext = useCallback(
    (song) => dispatch(PlayQueueActions.insertItemNext(song)),
    [dispatch]
  )

  const handlePlayLater = useCallback(
    (song) => dispatch(PlayQueueActions.insertItemAtBottom(song)),
    [dispatch]
  )

  const handleAddToPlaylist = useCallback(
    (playlist, track) =>
      dispatch(
        PlaylistThunks.addTracks(playlist.playlistid, [track.trackid], () =>
          dispatch(
            NotificationActions.broadcastMessage(
              { songTitle: track.title, playlistTitle: playlist.name },
              NotificationType.addedToPlaylist
            )
          )
        )
      ),
    [dispatch]
  )

  const handleCreatePlaylist = useCallback(
    (track) => {
      dispatch(PlaylistActions.setSongsToAdd([track.trackid]))
      dispatch(ModalActions.openPlaylistModal('search_results'))
    },
    [dispatch]
  )

  const handleShowModal = useCallback(
    (release) =>
      dispatch(
        ReleaseThunks.getDetail(release.releaseid, (releaseDetail) => {
          dispatch(ReleaseActions.toggleReleaseInModal(releaseDetail))
        })
      ),
    [dispatch]
  )

  const handleToggleModal = useCallback(
    ({ trackid }) => {
      setShowModal((prev) => (trackid === modalTrack ? !prev : true))
      setModalTrack((prev) => (trackid === prev ? prev : trackid))
    },
    [modalTrack]
  )

  const handleCloseModal = useCallback(
    ({ trackid }) => {
      if (trackid === modalTrack) {
        setShowModal(false)
        setModalTrack(null)
      }
    },
    [modalTrack]
  )

  const getTopResults = useCallback(
    () => getTopResultsUtil(response, 6),
    [response]
  )

  const handleLoadMoreProfileResults = useCallback(() => {
    loadMoreProfileResults(() => alert(t('alert.search-error')))
  }, [loadMoreProfileResults, t])

  const renderNoResults = () => (
    <NoResults
      title={`${t('search.noresults')} "${searched}"`}
      tip={t('search.noresults-tip')}
    />
  )

  const renderSongsTableModal = useCallback(
    (track, hovered) => {
      const { candownload } = track
      const isDownloadAllowed =
        candownload && browserHelpers.browserAllowsDownload
      const showCurrentModal = showModal && modalTrack === track.trackid
      const hideTrigger = !(hovered || showCurrentModal)
      return (
        <ReleaseOptions
          release={track}
          playlists={playlists}
          isOpen={showCurrentModal}
          hideTrigger={hideTrigger}
          isDownloadAllowed={isDownloadAllowed}
          onDownloadClick={handleDownloadSong}
          onPlayNextClick={handlePlayNext}
          onPlayLaterClick={handlePlayLater}
          onAddTrackToPlaylist={handleAddToPlaylist}
          onCreatePlaylist={handleCreatePlaylist}
          onToggle={() => handleToggleModal(track)}
        />
      )
    },
    [
      playlists,
      showModal,
      modalTrack,
      handleDownloadSong,
      handlePlayNext,
      handlePlayLater,
      handleAddToPlaylist,
      handleCreatePlaylist,
      handleToggleModal
    ]
  )

  const renderCategory = useCallback(
    (categoryKey, name) => {
      const playHandler =
        categoryKey === 'tracks' ? handlePlaySong : handlePlayRelease

      const renderItem = (item, index) => {
        if (categoryKey === 'labels') {
          return (
            <HalfCell key={index + 1}>
              <Link
                push="true"
                state={{ searchBackTo }}
                to={`/search/releases/label?q=${encodeURIComponent(
                  item.label
                )}`}
              >
                <ItemPreview
                  title={item.label}
                  subtitle={`${item.releases} Releases`}
                  image={item.logo}
                  fallback={labelPlaceholderLogo}
                />
              </Link>
            </HalfCell>
          )
        }
        if (categoryKey === 'artists') {
          return (
            <HalfCell key={index + 1}>
              <Link
                push="true"
                state={{ searchBackTo }}
                to={`/search/releases/artist?q=${encodeURIComponent(
                  item.artist
                )}`}
              >
                <ItemPreview
                  title={item.artist}
                  image={item.thumbnail}
                  fallback={labelPlaceholderLogo}
                />
              </Link>
            </HalfCell>
          )
        }
        return (
          <HalfCell key={index + 1}>
            <ItemPreview
              buttons
              item={item}
              title={item.artist}
              subtitle={item.title}
              image={item.coverart}
              fallback={placeholderLogo}
              onClick={() => {
                trackSearchEvent?.({
                  event: SEARCH_TRACKING_EVENTS.RESULT_INTERACTION,
                  action: SEARCH_ACTIONS.RESULT_CLICK,
                  label: item.title || item.artist || '',
                  platform,
                  source: 'search_page',
                  type: 'result',
                  result_category: categoryKey,
                  result_id:
                    item.trackid ||
                    item.releaseid ||
                    item.artistid ||
                    item.labelid ||
                    '',
                  result_rank: index + 1,
                  search_query: searched || '',
                  search_profile: profile || '',
                  search_query_length: (searched || '').length
                })
                handleShowModal(item)
              }}
              onPlay={playHandler}
            />
          </HalfCell>
        )
      }

      return (
        <CategorySection
          title={name}
          categoryKey={categoryKey}
          items={response[categoryKey]}
          searched={searched}
          renderItem={renderItem}
        />
      )
    },
    [
      handlePlayRelease,
      handlePlaySong,
      handleShowModal,
      platform,
      profile,
      response,
      searchBackTo,
      searched,
      trackSearchEvent
    ]
  )

  const renderTopResults = () => {
    const topResults = useAISearch ? response?.combined || [] : getTopResults()

    if (isMobile) {
      return (
        <TopResultsMobile
          results={topResults}
          useAISearch={useAISearch}
          loading={loading}
          renderNoResults={renderNoResults}
          onShowModal={handleShowModal}
          onPlayRelease={handlePlayRelease}
          onDownloadRelease={handleDownloadRelease}
          onPlayTrack={handlePlaySong}
          onDownloadTrack={handleDownloadSong}
        />
      )
    }
    return (
      <TopResultsDesktop
        results={topResults}
        useAISearch={useAISearch}
        loading={loading}
        renderNoResults={renderNoResults}
        onShowModal={handleShowModal}
        onPlayRelease={handlePlayRelease}
        onDownloadRelease={handleDownloadRelease}
        onPlayTrack={handlePlaySong}
        onDownloadTrack={handleDownloadSong}
        renderCategory={renderCategory}
      />
    )
  }

  // Mobile-only: render category items in a vertical list using ResultRowMobile
  const renderCategoryMobileList = useCallback(
    (categoryKey) => {
      const items = (response[categoryKey] || []).map((item, index) => (
        <ResultRowMobile
          key={`${item} ${index + 1}`}
          type={categoryKey}
          item={item}
          onShowModal={handleShowModal}
          onPlayRelease={handlePlayRelease}
          onDownloadRelease={handleDownloadRelease}
          onPlayTrack={handlePlaySong}
          onDownloadTrack={handleDownloadSong}
        />
      ))

      if (!loading && items.length === 0) return renderNoResults()
      return <MobileTopList>{items}</MobileTopList>
    },
    [
      response,
      loading,
      handleShowModal,
      handlePlayRelease,
      handleDownloadRelease,
      handlePlaySong,
      handleDownloadSong
    ]
  )

  const renderCategoryResults = useCallback(
    (categoryKey) => {
      let table
      let title
      let cells

      switch (categoryKey) {
        case 'releases': {
          cells = response.releases.map((item, index) => (
            <GridColumn key={`${item} ${index + 1}`}>
              <ReleaseSearchCard
                release={item}
                onShowModal={handleShowModal}
                handlePlayRelease={handlePlayRelease}
                handleDownloadRelease={handleDownloadRelease}
              />
            </GridColumn>
          ))
          table = (
            <ReleasesTable
              showPlayQueueOptions
              showMarkets
              downloadSource="search_results_releases_table"
              playSource={PLAY_SOURCES.SEARCH}
              items={response.releases}
            />
          )
          title = t('search.releases')
          break
        }
        case 'tracks': {
          cells = response.tracks.map((item, index) => (
            <GridColumn key={`${item} ${index + 1}`}>
              <TrackSearchCard
                track={item}
                onShowModal={handleShowModal}
                handlePlayTrack={handlePlaySong}
                handleDownloadTrack={handleDownloadSong}
              />
            </GridColumn>
          ))
          table = (
            <SongsTable
              showPlayQueueOptions
              showMarkets
              downloadSource="search_results_songs_table"
              playSource={PLAY_SOURCES.SEARCH}
              items={response.tracks}
              columns={[
                SongTableColumns.coverart,
                SongTableColumns.artist,
                SongTableColumns.title,
                SongTableColumns.duration,
                SongTableColumns.available,
                SongTableColumns.labels,
                SongTableColumns.genres,
                SongTableColumns.markets
              ]}
              renderModal={renderSongsTableModal}
              onHoverStop={handleCloseModal}
            />
          )
          title = t('search.songs')
          break
        }
        case 'artists': {
          cells = response.artists.map((item, index) => (
            <GridColumn key={`${item} ${index + 1}`}>
              <ArtistSearchCard artist={item} />
            </GridColumn>
          ))
          table = <ArtistsTable items={response.artists} />
          title = t('search.artists')
          break
        }
        case 'labels': {
          cells = response.labels.map((item, index) => (
            <GridColumn key={`${item} ${index + 1}`}>
              <LabelSearchCard label={item} />
            </GridColumn>
          ))
          table = <LabelsTable items={response.labels} />
          title = t('search.labels')
          break
        }
        default: {
          table = null
          title = null
          break
        }
      }

      return (
        <SeeAllContainer>
          <Tonality.UIText textStyle="label" color={theme.colors.white0}>
            {profile === 'artist'
              ? `${t('release-list.artist')}: ${searched}`
              : ''}
            {profile === 'label'
              ? `${t('release-list.label')}: ${searched}`
              : ''}
            {profile ? '' : `${t('search.see-all')} ${title}: ${searched}`}
          </Tonality.UIText>
          {!loading &&
            (profile && categoryKey === SearchCategory.releases ? (
              <InfiniteScroll
                dataLength={response.releases.length}
                hasMore={hasMoreProfileResults}
                loader={<div />}
                next={handleLoadMoreProfileResults}
                scrollableTarget="search-results-scroll-container"
              >
                {isListView ? table : <div>{cells}</div>}
              </InfiniteScroll>
            ) : isListView ? (
              table
            ) : (
              <div>{cells}</div>
            ))}
        </SeeAllContainer>
      )
    },
    [
      response,
      t,
      profile,
      searched,
      loading,
      isListView,
      handleShowModal,
      handlePlayRelease,
      handleDownloadRelease,
      handlePlaySong,
      handleDownloadSong,
      renderSongsTableModal,
      handleCloseModal,
      hasMoreProfileResults,
      handleLoadMoreProfileResults,
      searchBackTo
    ]
  )

  const renderContent = () => (
    <ContentWrapper
      data-cy="search-page"
      data-responsive-breakpoints={responsiveBreakpointsVersion}
      data-responsive-breakpoints-page={RESPONSIVE_BREAKPOINT_PAGES.SEARCH}
    >
      <ScrollContainer
        id="search-results-scroll-container"
        data-cy="search-results"
        $hidden={isSearching}
      >
        {/* Mobile: Tabs + TabPanels (desktop path below) */}
        {isMobile && !isSearching ? (
          <Tonality.Tabs
            index={mobileTabIndex}
            onChange={handleMobileTabChange}
            setting="player"
          >
            <MobileTabsContainer>
              <CompactTabList>
                <Tonality.Tab label={useAISearch ? t('search.ai-results') : t('search.top-results')} />
                <Tonality.Tab label={t('search.songs')} />
                <Tonality.Tab label={t('search.artists')} />
                <Tonality.Tab label={t('search.releases')} />
                <Tonality.Tab label={t('search.labels')} />
              </CompactTabList>
            </MobileTabsContainer>
            <Tonality.TabPanels>
              <Tonality.TabPanel>{renderTopResults()}</Tonality.TabPanel>
              <Tonality.TabPanel>
                {renderCategoryMobileList(SearchCategory.tracks)}
              </Tonality.TabPanel>
              <Tonality.TabPanel>
                {renderCategoryMobileList(SearchCategory.artists)}
              </Tonality.TabPanel>
              <Tonality.TabPanel>
                {renderCategoryMobileList(SearchCategory.releases)}
              </Tonality.TabPanel>
              <Tonality.TabPanel>
                {renderCategoryMobileList(SearchCategory.labels)}
              </Tonality.TabPanel>
            </Tonality.TabPanels>
          </Tonality.Tabs>
        ) : (
          <>
            {selectedCategory === SearchCategory.top
              ? renderTopResults()
              : renderCategoryResults(selectedCategory)}
          </>
        )}
      </ScrollContainer>
      {isSearching && (
        <Overlay>
          <RecentlySearched />
        </Overlay>
      )}
    </ContentWrapper>
  )

  return (
    <ResponsiveBreakpointsProvider
      value={RESPONSIVE_BREAKPOINT_VERSIONS.LEGACY}
    >
      <ResponsiveLayout
        content={
          <ResponsiveBreakpointsProvider value={responsiveBreakpointsVersion}>
            {renderContent()}
          </ResponsiveBreakpointsProvider>
        }
        menu={
          <ReleasesMenu
            searched={searched}
            onSearchingChange={(value) =>
              value ? handleSearchFocus() : handleSearchUnfocus()
            }
          />
        }
      />
    </ResponsiveBreakpointsProvider>
  )
}

const Search = () => {
  const { t } = useTranslation()

  return (
    <ResponsiveBreakpointsRolloutBoundary
      fallback={
        <ResponsiveBreakpointsProvider
          value={RESPONSIVE_BREAKPOINT_VERSIONS.LEGACY}
        >
          <ResponsiveLayout
            content={
              <div className="p-8 text-white" aria-live="polite" role="status">
                {t('general.loading')}
              </div>
            }
          />
        </ResponsiveBreakpointsProvider>
      }
      page={RESPONSIVE_BREAKPOINT_PAGES.SEARCH}
    >
      <SearchContent />
    </ResponsiveBreakpointsRolloutBoundary>
  )
}

export default React.memo(Search)
